import { Alert, Box, Chip, List, ListItem, Typography, styled, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useGetLocalityLicenseActiveInactiveLogs } from 'src/api/customer-license'
import { CenteredSpinner } from 'src/components/Spinner'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { getLocalFormattedDateTimeFromUTCString } from 'src/utils/function'
import { getLocalityActivityLogChipPresentation } from './LocalityActivityLogModal.action'

type LocalityActivityLogModalProps = ShowDialogProps & {
  customerId: string | null
  localityId: number | null
  localityName: string
}

export const LocalityActivityLogModal = ({
  pop: _pop,
  customerId,
  localityId,
}: LocalityActivityLogModalProps) => {
  const theme = useTheme()
  const { t } = useTranslation(['admin-license', 'activity-log'])

  const { data, isLoading, isError, isFetching } = useGetLocalityLicenseActiveInactiveLogs(customerId, localityId, {
    enabled: !!customerId && localityId != null && localityId > 0,
  })

  const rows = data ?? []

  return (
    <Box sx={{ minHeight: 200, maxHeight: { xs: '62vh', sm: '56vh' }, overflow: 'auto', px: 0 }}>
      {(isLoading || isFetching) && !data?.length ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CenteredSpinner />
        </Box>
      ) : isError ? (
        <Alert severity="error">{t('activityLogLoadError')}</Alert>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }} role="status">
          {t('activityLogEmpty')}
        </Typography>
      ) : (
        <List dense disablePadding aria-label={String(t('activityLogListAria'))}>
          {rows.map(logEntry => {
            const chipPresentation = getLocalityActivityLogChipPresentation(logEntry.activityType, t, theme)
            return (
              <ActivityLogListItem key={logEntry.id}>
                <Flex.Row gap={1}>
                  <ActivityLogStatusChip
                    label={chipPresentation.label}
                    size="small"
                    logBackground={chipPresentation.backgroundColor}
                    logForeground={chipPresentation.foregroundColor}
                  />
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.75, display: 'block', flex: 1 }}
                  >
                    {getLocalFormattedDateTimeFromUTCString(logEntry.createdAt, 'YYYY-MM-DD HH:mm:ss')}
                    {logEntry.performedByUserName ? ` · ${logEntry.performedByUserName}` : ` · ${t('unknownUser')}`}
                  </Typography>
                </Flex.Row>
              </ActivityLogListItem>
            )
          })}
        </List>
      )}
    </Box>
  )
}

const ActivityLogListItem = styled(ListItem)(({ theme }) => ({
  alignItems: 'stretch',
  flexDirection: 'column',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}))

const ActivityLogStatusChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'logBackground' && prop !== 'logForeground',
})<{ logBackground: string; logForeground: string }>(({ theme, logBackground, logForeground }) => ({
  alignSelf: 'flex-start',
  height: 26,
  paddingInline: theme.spacing(0.5),
  fontWeight: 600,
  width: 100,
  fontSize: 11,
  borderRadius: theme.spacing(1.5),
  backgroundColor: logBackground,
  color: logForeground,
  '& .MuiChip-label': {
    paddingInline: 0,
  },
}))
